import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Typography,
  Box,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import { CheckCircle, AlertTriangle, Clock, User, Home } from 'lucide-react';
import { formatTherapistsDisplay } from '../utils/therapistDisplay';

const CombinedPreviewModal = ({ 
  open, 
  onClose, 
  schedulePreview,
  upgradePreview,
  originalBooking,
  onConfirm,
  isLoading = false,
  confirmError = null,
  showSuccess = false,
  successMessage = ''
}) => {
  const [upgradeStatus, setUpgradeStatus] = useState('payable');
  const [discountType, setDiscountType] = useState('amount');
  const [discountValue, setDiscountValue] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const hasUpgrade = upgradePreview?.is_upgrade;
  const hasScheduleChange = schedulePreview !== null;

  const handleConfirm = () => {
    if (isLoading) return;

    // If there's an upgrade, validate billing options
    if (hasUpgrade) {
      if (upgradeStatus === 'complimentary' && !reason.trim()) {
        setError('Please provide a reason for complimentary upgrade');
        return;
      }
      if (upgradeStatus === 'discounted') {
        const discount = parseFloat(discountValue);
        if (!discountValue || isNaN(discount) || discount <= 0) {
          setError(`Please enter a valid discount ${discountType === 'percentage' ? 'percentage' : 'amount'}`);
          return;
        }
        if (discountType === 'percentage' && discount >= 100) {
          setError('Discount percentage must be less than 100%');
          return;
        }
        if (discountType === 'amount' && discount >= parseFloat(upgradePreview.upgrade_amount)) {
          setError('Discount amount cannot be equal to or greater than upgrade amount');
          return;
        }
        if (!reason.trim()) {
          setError('Please provide a reason for discounted upgrade');
          return;
        }
      }

      // Calculate actual discount amount
      let discountAmount = 0;
      if (upgradeStatus === 'discounted') {
        const upgradeAmt = parseFloat(upgradePreview.upgrade_amount);
        if (discountType === 'percentage') {
          discountAmount = upgradeAmt * (parseFloat(discountValue) / 100);
        } else {
          discountAmount = parseFloat(discountValue);
        }
      }

      const approvalData = {
        upgrade_status: upgradeStatus,
        upgrade_amount: parseFloat(upgradePreview.upgrade_amount),
        upgrade_discount_amount: discountAmount,
        upgrade_reason: reason.trim(),
        approved_by: 'Current User',
        approved_at: new Date().toISOString()
      };

      // Reset modal state
      setUpgradeStatus('payable');
      setDiscountType('amount');
      setDiscountValue('');
      setReason('');
      setError('');

      // Call onConfirm - parent will handle closing
      onConfirm(schedulePreview, approvalData);
    } else {
      // No upgrade, just confirm schedule change
      
      // Reset modal state
      setUpgradeStatus('payable');
      setDiscountType('amount');
      setDiscountValue('');
      setReason('');
      setError('');

      // Call onConfirm - parent will handle closing
      onConfirm(schedulePreview, null);
    }

    // Don't call handleClose() - let parent handle modal closing
  };

  const handleClose = () => {
    setUpgradeStatus('payable');
    setDiscountType('amount');
    setDiscountValue('');
    setReason('');
    setError('');
    onClose();
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const calculateDiscountAmount = () => {
    if (!hasUpgrade || upgradeStatus !== 'discounted' || !discountValue) return 0;
    
    const upgradeAmt = parseFloat(upgradePreview.upgrade_amount);
    const discount = parseFloat(discountValue) || 0;
    
    if (discountType === 'percentage') {
      return upgradeAmt * (discount / 100);
    }
    return discount;
  };

  const calculateFinalAmount = () => {
    if (!hasUpgrade) return 0;
    if (upgradeStatus === 'complimentary') return 0;
    
    const upgradeAmt = parseFloat(upgradePreview.upgrade_amount);
    const taxRate = parseFloat(upgradePreview.tax_rate);
    
    if (upgradeStatus === 'discounted') {
      const discountAmt = calculateDiscountAmount();
      const netUpgrade = upgradeAmt - discountAmt;
      const tax = netUpgrade * (taxRate / 100);
      return netUpgrade + tax;
    }
    
    return parseFloat(upgradePreview.total_additional_cost);
  };

  if (!schedulePreview) return null;

  const previewTherapistsLabel = formatTherapistsDisplay(
    schedulePreview.therapists,
    schedulePreview.therapist,
  );
  const originalTherapistsLabel = formatTherapistsDisplay(
    originalBooking?.therapists,
    originalBooking?.therapist,
  );
  const therapistCount = schedulePreview.therapists?.length || (schedulePreview.therapist ? 1 : 0);
  const therapistFieldLabel = therapistCount > 1 ? 'Therapists' : 'Therapist';
  const fromTreatmentName =
    originalBooking?.treatment
    || originalBooking?.service
    || upgradePreview?.original_treatment_name;
  const packageEntitlementName = upgradePreview?.package_entitlement_treatment_name;
  const showPackageEntitlementNote = Boolean(
    hasUpgrade
    && packageEntitlementName
    && fromTreatmentName
    && packageEntitlementName.toLowerCase() !== fromTreatmentName.toLowerCase(),
  );

  // Show success state
  if (showSuccess) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#e8f5e9', pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
            <CheckCircle size={32} color="#4caf50" />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e7d32' }}>
              Appointment Confirmed!
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Your appointment has been successfully updated
            </Typography>

            {/* Appointment Details */}
            <Box sx={{ bgcolor: '#f5f5f5', p: 3, borderRadius: 2, textAlign: 'left' }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#1976d2' }}>
                📋 Appointment Details
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Treatment</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {schedulePreview.treatment}
                  </Typography>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Date</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {new Date(schedulePreview.date).toLocaleDateString('en-US', { 
                        weekday: 'short', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Time</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {schedulePreview.startTime} - {schedulePreview.endTime}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">{therapistFieldLabel}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {previewTherapistsLabel}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Room</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {schedulePreview.room}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {successMessage && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {successMessage}
              </Alert>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            onClick={onClose}
            variant="contained"
            sx={{ 
              bgcolor: '#4caf50', 
              color: 'white', 
              minWidth: 120,
              '&:hover': { bgcolor: '#45a049' } 
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={isLoading}
    >
      <DialogTitle sx={{ bgcolor: hasUpgrade ? '#fff3e0' : '#e8f5e9', pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {hasUpgrade ? (
            <AlertTriangle size={24} color="#E4C984" />
          ) : (
            <CheckCircle size={24} color="#4caf50" />
          )}
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {hasUpgrade ? 'Treatment Change with Upgrade' : 'Treatment Change Preview'}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Schedule Preview Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#1976d2' }}>
            📅 Updated Schedule
          </Typography>
          
          <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, borderLeft: '4px solid #1976d2' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              {/* Time */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Clock size={16} color="#666" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Time</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {schedulePreview.startTime} - {schedulePreview.endTime}
                  </Typography>
                </Box>
              </Box>

              {/* Therapist(s) */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <User size={16} color="#666" />
                <Box>
                  <Typography variant="caption" color="text.secondary">{therapistFieldLabel}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {originalTherapistsLabel !== previewTherapistsLabel ? (
                      <span>
                        <span style={{ textDecoration: 'line-through', color: '#999' }}>
                          {originalTherapistsLabel}
                        </span>
                        {' → '}
                        <span style={{ color: '#1976d2' }}>{previewTherapistsLabel}</span>
                      </span>
                    ) : (
                      previewTherapistsLabel
                    )}
                  </Typography>
                </Box>
              </Box>

              {/* Room */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Home size={16} color="#666" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Room</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {originalBooking.room !== schedulePreview.room ? (
                      <span>
                        <span style={{ textDecoration: 'line-through', color: '#999' }}>
                          {originalBooking.room}
                        </span>
                        {' → '}
                        <span style={{ color: '#1976d2' }}>{schedulePreview.room}</span>
                      </span>
                    ) : (
                      schedulePreview.room
                    )}
                  </Typography>
                </Box>
              </Box>

              {/* Treatment */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle size={16} color="#666" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Treatment</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                    {schedulePreview.treatment}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Upgrade Section (if applicable) */}
        {hasUpgrade && (
          <>
            <Divider sx={{ my: 3 }} />
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#E4C984' }}>
                ⬆️ Service Upgrade
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Box sx={{ flex: 1, p: 2, bgcolor: '#fafafa', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">From</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {fromTreatmentName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {upgradePreview.original_tier_level} • {formatCurrency(upgradePreview.original_tier_value)}
                  </Typography>
                  {showPackageEntitlementNote && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Package inclusion: {packageEntitlementName}
                      {upgradePreview.package_entitlement_tier_value != null
                        ? ` • ${formatCurrency(upgradePreview.package_entitlement_tier_value)}`
                        : ''}
                    </Typography>
                  )}
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
                  <Typography variant="h6" color="text.secondary">→</Typography>
                </Box>
                
                <Box sx={{ flex: 1, p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #E4C984' }}>
                  <Typography variant="caption" color="text.secondary">To</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {upgradePreview.new_treatment_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {upgradePreview.new_tier_level} • {formatCurrency(upgradePreview.new_tier_value)}
                  </Typography>
                </Box>
              </Box>

              {/* Pricing Summary */}
              <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Upgrade Amount:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatCurrency(upgradePreview.upgrade_amount)}
                  </Typography>
                </Box>
                
                {upgradeStatus === 'discounted' && discountValue && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: '#2e7d32' }}>
                      Discount ({discountType === 'percentage' ? `${discountValue}%` : formatCurrency(discountValue)}):
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                      - {formatCurrency(calculateDiscountAmount())}
                    </Typography>
                  </Box>
                )}
                
                {upgradeStatus === 'complimentary' && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: '#2e7d32' }}>
                      Complimentary Discount:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                      - {formatCurrency(upgradePreview.upgrade_amount)}
                    </Typography>
                  </Box>
                )}
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">
                    Tax ({upgradePreview.tax_rate}%):
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {upgradeStatus === 'complimentary' 
                      ? '₹0.00' 
                      : upgradeStatus === 'discounted' && discountValue
                      ? formatCurrency((parseFloat(upgradePreview.upgrade_amount) - calculateDiscountAmount()) * (parseFloat(upgradePreview.tax_rate) / 100))
                      : formatCurrency(upgradePreview.tax_on_upgrade)
                    }
                  </Typography>
                </Box>
                
                <Divider sx={{ my: 1 }} />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Guest Pays:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: '#1976d2' }}>
                    {formatCurrency(calculateFinalAmount())}
                  </Typography>
                </Box>
              </Box>

              {/* Billing Options */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                How should this upgrade be billed?
              </Typography>

              <RadioGroup value={upgradeStatus} onChange={(e) => setUpgradeStatus(e.target.value)}>
                <FormControlLabel
                  value="payable"
                  control={<Radio sx={{ color: '#E4C984', '&.Mui-checked': { color: '#E4C984' }, padding: '0px', marginRight: '8px' }} />}
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: '20px' }}>
                        Payable
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Guest pays full upgrade amount ({formatCurrency(upgradePreview.total_additional_cost)})
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', mb: 1.5 }}
                />
                
                <FormControlLabel
                  value="complimentary"
                  control={<Radio sx={{ color: '#E4C984', '&.Mui-checked': { color: '#E4C984' }, padding: '0px', marginRight: '8px' }} />}
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: '20px' }}>
                        Complimentary
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        No charge to guest (requires reason)
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', mb: 1.5 }}
                />
                
                <FormControlLabel
                  value="discounted"
                  control={<Radio sx={{ color: '#E4C984', '&.Mui-checked': { color: '#E4C984' }, padding: '0px', marginRight: '8px' }} />}
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: '20px' }}>
                        Discounted
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Partial charge with discount (requires reason)
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', mb: 1.5 }}
                />
              </RadioGroup>

              {/* Discount Input */}
              {upgradeStatus === 'discounted' && (
                <Box sx={{ mt: 2, pl: 4 }}>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Button
                      variant={discountType === 'amount' ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => {
                        setDiscountType('amount');
                        setDiscountValue('');
                      }}
                      sx={{
                        flex: 1,
                        bgcolor: discountType === 'amount' ? '#E4C984' : 'transparent',
                        color: discountType === 'amount' ? '#171717' : '#E4C984',
                        borderColor: '#E4C984',
                        '&:hover': {
                          bgcolor: discountType === 'amount' ? '#cbb16f' : 'rgba(228, 201, 132, 0.1)',
                          borderColor: '#E4C984'
                        }
                      }}
                    >
                      Amount (₹)
                    </Button>
                    <Button
                      variant={discountType === 'percentage' ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => {
                        setDiscountType('percentage');
                        setDiscountValue('');
                      }}
                      sx={{
                        flex: 1,
                        bgcolor: discountType === 'percentage' ? '#E4C984' : 'transparent',
                        color: discountType === 'percentage' ? '#171717' : '#E4C984',
                        borderColor: '#E4C984',
                        '&:hover': {
                          bgcolor: discountType === 'percentage' ? '#cbb16f' : 'rgba(228, 201, 132, 0.1)',
                          borderColor: '#E4C984'
                        }
                      }}
                    >
                      Percentage (%)
                    </Button>
                  </Box>

                  <TextField
                    fullWidth
                    size="small"
                    label={discountType === 'percentage' ? 'Discount Percentage' : 'Discount Amount'}
                    type="number"
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    placeholder={discountType === 'percentage' ? 'e.g. 10' : 'Enter amount'}
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1 }}>
                        {discountType === 'percentage' ? '%' : '₹'}
                      </Typography>,
                    }}
                    inputProps={{
                      min: 0,
                      max: discountType === 'percentage' ? 99 : parseFloat(upgradePreview.upgrade_amount),
                      step: discountType === 'percentage' ? 1 : 0.01
                    }}
                    sx={{ mb: 1 }}
                  />
                </Box>
              )}

              {/* Reason Input */}
              {(upgradeStatus === 'complimentary' || upgradeStatus === 'discounted') && (
                <Box sx={{ mt: 2, pl: 4 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    multiline
                    rows={2}
                    placeholder="Enter reason for complimentary/discounted upgrade"
                  />
                </Box>
              )}
            </Box>
          </>
        )}

        {/* Error Message */}
        {(error || confirmError) && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error || confirmError}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit" disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : null}
          sx={{ 
            bgcolor: '#E4C984', 
            color: '#171717', 
            '&:hover': { bgcolor: '#cbb16f' },
            fontWeight: 600
          }}
        >
          {isLoading
            ? 'Applying...'
            : hasUpgrade
              ? 'Approve & Apply Changes'
              : 'Apply Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CombinedPreviewModal;

